import React, { useRef } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { usePropertiesPanelAutoOpenSection } from '@/hooks/use-properties-panel-auto-open-section'
import { useActiveSelectionOptional } from '@/components/app/editor/active-object-context'
import { useEditorCommands } from '@/contexts/editor-commands-context'
import { useDocumentLayout } from '@/contexts/document-layout-context'
import { useEditorTools } from '@/contexts/editor-tools-context'
import { describePropertiesPanelSelection } from '@/lib/panels/describe-properties-panel-selection'
import { useSpellCheck } from './spell-check/hooks/use-spell-check'
import { PropertiesPanel } from './root-panel/components/properties-panel'
import { ToolContent } from './root-panel/components/tool-content'
import { ToolRail } from './root-panel/components/tool-rail'
import { TOOL_RAIL_ITEMS } from './root-panel/constants/tool-rail-items'
import { useLeftPanelImperativeHandle } from './root-panel/hooks/use-left-panel-imperative-handle'
import { useLeftPanelVisibility } from './root-panel/hooks/use-left-panel-visibility'
import { useLeftPanelVisibilitySync } from './root-panel/hooks/use-left-panel-visibility-sync'
import { useSelectToolSections } from './root-panel/hooks/use-select-tool-sections'
import { useShapeToolState } from './root-panel/hooks/use-shape-tool-state'
import { useToolActivation } from './root-panel/hooks/use-tool-activation'
import { useToolShortcuts } from './root-panel/hooks/use-tool-shortcuts'
import { useToolSwitchResets } from './root-panel/hooks/use-tool-switch-resets'
import type { LeftPanelProps } from './root-panel/types/left-panel-props'
import type { LeftPanelRef } from './root-panel/types/left-panel-ref'

const LeftPanelInner = React.forwardRef<LeftPanelRef, LeftPanelProps>(
  ({ className, ...toolProps }, ref) => {
    const {
      selectedShape: propSelectedShape,
      setSelectedShape: propSetSelectedShape,
      selectedPenTool: propSelectedPenTool,
      setSelectedPenTool: propSetSelectedPenTool,
      selectedPointType: propSelectedPointType,
      setSelectedPointType: propSetSelectedPointType,
      isPanModeActive,
      activeItem,
      setActiveItem,
      activateTool: onToolActivate,
      isNodeEditMode,
    } = useEditorTools()
    const { exitNodeEditMode: onExitNodeEditMode } = useEditorCommands()
    // Hook call order below is load-bearing: it keeps every effect in its original relative
    // order (section effects → useIsMobile → right-panel collapse → shortcuts → visibility sync →
    // tool-switch resets → auto-open).

    // The selection without live position (`useActiveSelection`): both the section state's
    // selection identity and the subtitle line need only what is selected, so a drag that moves
    // the selection doesn't re-render the panel.
    const activeObjectCtx = useActiveSelectionOptional()
    const sections = useSelectToolSections({ activeItem, activeObjectCtx })
    const isMobile = useIsMobile()
    const shapeTool = useShapeToolState({
      selectedShape: propSelectedShape,
      setSelectedShape: propSetSelectedShape,
      selectedPenTool: propSelectedPenTool,
      setSelectedPenTool: propSetSelectedPenTool,
      selectedPointType: propSelectedPointType,
      setSelectedPointType: propSetSelectedPointType,
    })
    const selectToolButtonRef = useRef<HTMLButtonElement>(null)
    const typeToolButtonRef = useRef<HTMLButtonElement>(null)
    const {
      isExpanded,
      setIsExpanded,
      mobileOpen,
      setMobileOpen,
      revealPanel,
      hidePanel,
      togglePanel,
    } = useLeftPanelVisibility(isMobile)
    const { activateTool, handleToolRailClick, handleTogglePanel } = useToolActivation({
      activeItem,
      setActiveItem,
      onToolActivate,
      isNodeEditMode,
      onExitNodeEditMode,
      resetShapeCreation: shapeTool.resetShapeCreation,
      openSelectToolSection: sections.openSelectToolSection,
      collapsePropertiesPanelAllSections: sections.collapsePropertiesPanelAllSections,
      revealPanel,
      togglePanel,
    })

    useLeftPanelImperativeHandle(ref, {
      activeItem,
      activateTool,
      propSetSelectedShape,
      resetShapeCreation: shapeTool.resetShapeCreation,
      openSelectToolSectionRaw: sections.openSelectToolSectionRaw,
      collapsePropertiesPanelSectionsTemporarily:
        sections.collapsePropertiesPanelSectionsTemporarily,
      revealPanel,
      hidePanel,
      selectToolButtonRef,
      typeToolButtonRef,
    })

    useToolShortcuts({
      isPanModeActive,
      selectedShape: shapeTool.selectedShape,
      selectedPenTool: shapeTool.selectedPenTool,
      setSelectedShape: shapeTool.setSelectedShape,
      setSelectedPenTool: shapeTool.setSelectedPenTool,
      resetShapeCreation: shapeTool.resetShapeCreation,
      activateTool,
      openSelectToolSection: sections.openSelectToolSection,
      revealPanel,
      selectToolButtonRef,
      typeToolButtonRef,
    })

    useLeftPanelVisibilitySync({
      isMobile,
      isExpanded,
      mobileOpen,
      setIsExpanded,
      setMobileOpen,
      setActiveItem,
    })

    useToolSwitchResets({
      activeItem,
      setSelectedPenTool: shapeTool.setSelectedPenTool,
    })

    // Lives here, not in SpellCheckPanel, so fixes survive switching tools and back.
    const { spreadId, pageViewMode, rightPageOriginX } = useDocumentLayout()
    const spellCheck = useSpellCheck({
      isActive: activeItem === 'spellcheck',
      shapeManagerRef: toolProps.shapeManagerRef,
      spreadId,
      spineX: pageViewMode === 'spread' ? rightPageOriginX : null,
      textPanelRevision: toolProps.textPanelRevision,
      textChains: toolProps.textChains,
    })

    // Reuses ActiveObjectContext (already the source of truth for selection elsewhere in the
    // panel) rather than tracking selection separately just for this subtitle line.
    const selectionDescription = describePropertiesPanelSelection({
      activeItem,
      objectCount: activeObjectCtx?.activeObjects.length ?? 0,
      textBoxCount: activeObjectCtx?.activeTextBoxes.length ?? 0,
    })

    // Selection -> default Select-tool section (Object/Type/Photo) to auto-open, with
    // per-selection memory of the user's own open/close choice — see
    // `computePropertiesPanelSelectionSignature` and `usePropertiesPanelAutoOpenSection`'s doc
    // comments for the full rule set. Deliberately uses the raw setter (not the scroll-notifying
    // `openSelectToolSection` wrapper): auto-open is a side effect of the selection changing, not
    // a direct user action, so it must go through the scroll hook's own pin-aware selection
    // targeting instead of unconditionally jumping to the newly-opened section.
    usePropertiesPanelAutoOpenSection({
      selectionKey: sections.selectToolSelectionSignature.key,
      defaultSectionId: sections.selectToolSelectionSignature.defaultSectionId,
      openSectionIds: sections.selectToolOpenSectionIds,
      setOpenSectionIds: sections.setSelectToolOpenSectionIdsRaw,
      getExplicitSectionState: sections.getExplicitSectionState,
    })

    const toolLabel = TOOL_RAIL_ITEMS.find((item) => item.id === activeItem)?.label || 'Select'

    const toolContent = (
      <ToolContent
        {...toolProps}
        sections={{
          openSectionIds: sections.displayedSelectToolOpenSectionIds,
          onOpenSectionIdsChange: sections.handleOpenSectionIdsChange,
          registerSectionElement: sections.registerSectionElement,
        }}
        spellCheck={spellCheck}
      />
    )

    return (
      <>
        <div className="flex h-full overflow-hidden">
          {/* Left Sidebar (icon rail) - always visible */}
          <ToolRail
            className={className}
            activeItem={activeItem}
            isMobile={isMobile}
            isExpanded={isExpanded}
            mobileOpen={mobileOpen}
            isNodeEditMode={isNodeEditMode}
            selectToolButtonRef={selectToolButtonRef}
            typeToolButtonRef={typeToolButtonRef}
            shapeManagerRef={toolProps.shapeManagerRef}
            onBatchShapesCommitted={toolProps.onBatchShapesCommitted}
            onTogglePanel={handleTogglePanel}
            onToolClick={handleToolRailClick}
          />

          {/* Properties Panel - inline on desktop/tablet */}
          {!isMobile && isExpanded && (
            <PropertiesPanel
              variant="inline"
              toolLabel={toolLabel}
              description={selectionDescription}
              scrollContainerRef={sections.scrollContainerRef}
            >
              {toolContent}
            </PropertiesPanel>
          )}
        </div>

        {/* Properties Panel - floating on mobile; rail remains visible */}
        {isMobile && mobileOpen && (
          <PropertiesPanel
            variant="floating"
            toolLabel={toolLabel}
            description={selectionDescription}
            scrollContainerRef={sections.scrollContainerRef}
            onBackdropClick={() => setMobileOpen(false)}
          >
            {toolContent}
          </PropertiesPanel>
        )}
      </>
    )
  }
)

LeftPanelInner.displayName = 'LeftPanel'

/** Memoized: re-renders only when a prop changes or a context it reads does. */
export const LeftPanel = React.memo(LeftPanelInner)
